import { observer } from 'mobx-react';
import classNames from 'classnames';
import { RootStore, withInjectedRootStore } from 'providers';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import { useBreakpoint } from 'providers/BreakpointProvider';
import * as React from 'react';
import { SideTitle } from '../SideTitle';
import { getFaqs } from 'utils';
import s from './Faq.scss';
import { FaqItem } from './FaqItem';
import { FaqSideSubtitle } from './FaqSubtitle';
import { MediaDevices } from 'enums';

export enum FaqType {
  Default = 'default',
  Enterprise = 'enterprise',
  All = 'all',
}

export interface FaqProps {
  rootStore: RootStore;
  faqType?: FaqType;
}

const FaqsComp: React.FC<FaqProps> = ({
  faqType = FaqType.Default,
  rootStore,
}) => {
  const { theme } = usePageModeContext();
  const { currentBreakpoint } = useBreakpoint();
  const isMobile = currentBreakpoint === MediaDevices.Mobile;
  const { hasGoogleVoucher, country } = rootStore.appStore.appContext;
  const {
    isFranchPackagePicker,
    isJapanesePackagePicker,
    isEligibleForRevShare,
    experiments,
  } = rootStore.appStore;
  const localizedFaqs = isFranchPackagePicker || isJapanesePackagePicker;

  const getFaqItems = () => {
    const defaultFaqs = getFaqs({
      hasGoogleVoucher,
      theme,
      localizedFaqs,
      country,
      isEligibleForRevShare,
      experiments,
    }).map((faq) => (
      <FaqItem key={faq} faqCode={faq} isEnterpriseFaqs={false} />
    ));

    const enterpriseFaqs = getFaqs({
      isEnterprisePlanType: true,
      hasGoogleVoucher,
      theme,
      localizedFaqs,
      country,
    }).map((faq) => (
      <FaqItem
        key={faq}
        faqCode={faq}
        isEnterpriseFaqs={true}
      />
    ));

    switch (faqType) {
      case FaqType.All: {
        return [...defaultFaqs, ...enterpriseFaqs];
      }
      case FaqType.Enterprise: {
        return enterpriseFaqs;
      }
      default:
        return defaultFaqs;
    }
  };

  const faqs = getFaqItems();

  return (
    <div className={classNames(s.faqContainer, {
      [s.faqContainerCompact]: isMobile,
    })}>
      <div className={s.innerContainer}>
        <SideTitle titleKey='faq_section.faq_title'>
          <FaqSideSubtitle />
        </SideTitle>
        <div className={s.faqListContainer} data-testid='faq-list'>
          {faqs}
        </div>
      </div>
    </div>
  );
};

export const Faq = withInjectedRootStore(observer(FaqsComp));
